import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Select,
  Form,
  Input,
  Button,
  Row,
  Col,
  DatePicker,
  Upload,
  message,
  Card,
} from "antd";
import dayjs from "dayjs";
import { CloudUploadOutlined, DeleteOutlined } from "@ant-design/icons"; // Import DeleteOutlined
import { createIntimateClaimOpd, getEmployeeCode, getPatientName } from "../../Action/Actions";
import { getUserData } from "../login/LoginSlice";

const { Option } = Select;
const { Dragger } = Upload;

const docType = [
  { id: 1, name: "Prescription" },
  { id: 2, name: "Medicine Bills" },
  { id: 3, name: "Lab Report" },
  { id: 4, name: "Paid Receipt" },
  { id: 5, name: "Cancel Cheque" },
  { id: 6, name: "Other" },
];

const OpdForm = ({ selectedPolicyNumber,refPolicyId,initialEmployeeCode, initialEmployeeName }) => {
  const dispatch = useDispatch();
  const [formOPD] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const user = useSelector(getUserData);
  const [employeeCode, setEmployeeCode] = useState([]);
  const [patientName, setPatientName] = useState([]);
  const [documents, setDocuments] = useState([
    { key: Date.now(), document_type: null, upload_document: [] }
  ]);
 
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
     fetchEmployeeCode();
     
     if (initialEmployeeCode) {
       fetchPatientName(initialEmployeeCode);
     }
     formOPD.setFieldsValue({
      employee_code: initialEmployeeCode,
      patient_name: initialEmployeeName,
    });
   }, [selectedPolicyNumber, refPolicyId, formOPD, initialEmployeeCode, initialEmployeeName]);

   const fetchEmployeeCode = () => {
      dispatch(getEmployeeCode(user?.corporate_id))
        .then((data) => {
          const { Corporate_Employee } = data;
          setEmployeeCode(Corporate_Employee || []);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    const fetchPatientName = (employee_code) => {
      
      formOPD.setFieldsValue({ patient_name: undefined });
      dispatch(getPatientName(employee_code))
        .then((data) => {
          const { Corporate_Employee } = data;
          setPatientName(Corporate_Employee || []);
        })
        .catch((error) => {
          console.log(error);
        });
    };

   const onFinishIPD = (values) => {
    setIsSubmitted(true);
    const isDocumentsValid = documents.every(
      (doc) => doc.document_type && doc.upload_document.length > 0
    );

    if (!isDocumentsValid) {
      message.error("Please select a document type and upload the file for all document rows.");
      return; 
    }

    setLoading(true);
  
    const formData = new FormData();
    formData.append("ref_corporate_id", user?.corporate_id);
    formData.append("policy_no", selectedPolicyNumber);
    formData.append("user_id", user?.user_id);
    formData.append("employee_code", values?.employee_code);
    formData.append("patient_name", values?.patient_name);
    formData.append("mobile_number", values?.mobile_number);
    formData.append("email_id", values?.email_id);
    formData.append(
      "date_of_consultation",
      values?.consultant_date ? dayjs(values?.consultant_date).format("DD-MM-YYYY") : ""
    );
    formData.append("consultation_amount", values?.consultant_amount);
    formData.append("medicine_bill_amount", values?.medical_bill_amount);
    formData.append("diagnostic_bill_amount", values?.diagnostics_bill_amount);
    formData.append("total_claim_amount", values?.total_claim_amount);
    formData.append("reason_claim", values?.reason_for_claim);
    formData.append("hospital_name", values?.hospital_name);
    formData.append("hospital_address", values?.hospital_address);
    formData.append("ref_policy_id", refPolicyId);
    formData.append("portal_type", 2);
    // Removed duplicate ref_corporate_id append from original code
  
    documents.forEach((doc, i) => {
      formData.append(`documents[${i}][document_type]`, doc.document_type || "");
      if (doc.upload_document && doc.upload_document.length > 0 && doc.upload_document[0].originFileObj) {
        formData.append(`documents[${i}][doc_upload]`, doc.upload_document[0].originFileObj);
      }
    });

    dispatch(createIntimateClaimOpd(formData))
      .then((data) => {
        message.success(data?.message);
        setLoading(false);
        formOPD.resetFields();
        setDocuments([{ key: Date.now(), document_type: null, upload_document: [] }]);
        setIsSubmitted(false); // Reset submission state on success
      })
      .catch((error) => {
        setLoading(false);
        message.error("Claim submission failed. Please try again.");
      });
  };

  const handleDocumentTypeChange = (value, index) => {
    const updated = [...documents];
    updated[index].document_type = value;
    setDocuments(updated);
  };

  const handleUploadChange = (info, index) => {
    const updated = [...documents];
    updated[index].upload_document = info.fileList;
    setDocuments(updated);
  };

  const addDocument = () => {
    setDocuments([...documents, { key: Date.now(), document_type: null, upload_document: [] }]);
  };

  const removeDocument = (key) => {
    setDocuments(documents.filter((doc) => doc.key !== key));
  };

  const handleEmployeeCodeChange = (value) => {
    fetchPatientName(value);
  };

  return (
    <div>
      <Card>
        <Form form={formOPD} layout="vertical" onFinish={onFinishIPD}>
          <Row gutter={16}>
            {/* All other Form Items remain unchanged */}
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item
                label="Employee Code"
                name="employee_code"
                rules={[{ required: true, message: "Please Select Employee Code" }]}
              >
                <Select
                  showSearch
                  placeholder="Select Employee Code"
                  onChange={handleEmployeeCodeChange}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {employeeCode.map((employee) => (
                    <Option key={employee.id} value={employee.employee_code}>
                      {employee.employee_code}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item
                label="Patient Name"
                name="patient_name"
                rules={[{ required: true, message: "Please Select Patient Name" }]}
              >
                <Select
                  showSearch
                  placeholder="Select Patient Name"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                    {patientName.map((patient) => (
                    <Option key={patient.id} value={patient.employee_name}>
                      {patient.employee_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item
                name="mobile_number"
                label="Mobile Number"
                rules={[
                  { required: true, message: "Please enter Mobile Number" },
                  { pattern: /^[0-9]{10}$/, message: "Please input a valid 10-digit Mobile Number!" }
                ]}
              >
                <Input placeholder="Mobile Number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="email_id" label="Email ID" rules={[{ required: true, type: 'email', message: "Please enter a valid Email ID" }]}>
                <Input placeholder="Email ID" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="consultant_date" label="Date of Consultation" rules={[{ required: true, message: "Please Enter Date of Consultation" }]}>
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="DD-MM-YYYY" disabledDate={(current) => current && current > dayjs().endOf("day")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="consultant_amount" label="Consultation Amount" rules={[{ required: true, message: "Please enter Consultation Amount" }]}>
                <Input placeholder="Consultation Amount" type="number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="medical_bill_amount" label="Medicine Bill Amount" rules={[{ required: true, message: "Please enter Medicine Bill Amount" }]}>
                <Input placeholder="Medicine Bill Amount" type="number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="diagnostics_bill_amount" label="Diagnostics (X/ray, Scan) Bill Amount" rules={[{ required: true, message: "Please enter Diagnostics Bill Amount" }]}>
                <Input placeholder="Diagnostics Bill Amount" type="number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="total_claim_amount" label="Total Claim Amount" rules={[{ required: true, message: "Please enter Total Claim Amount" }]}>
                <Input placeholder="Total Claim Amount" type="number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="reason_for_claim" label="Reason For Claim" rules={[{ required: true, message: "Please enter Reason For Claim" }]}>
                <Input placeholder="Reason For Claim" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Form.Item name="hospital_name" label="Name of the Hospital" rules={[{ required: true, message: "Please enter Name of the Hospital" }]}>
                <Input placeholder="Name of the Hospital" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
              <Form.Item name="hospital_address" label="Hospital Address" rules={[{ required: true, message: "Please enter Hospital Address" }]}>
                <Input placeholder="Hospital Address" />
              </Form.Item>
            </Col>
          </Row>
          {documents.map((doc, index) => (
            <Row gutter={16} key={doc.key} style={{ alignItems: 'baseline' }}>
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Form.Item
                  label="Document Type"
                  required
                  // Only show error status if submission has been attempted and field is empty
                  validateStatus={isSubmitted && !doc.document_type ? 'error' : ''}
                  help={isSubmitted && !doc.document_type ? "Please Select Document Type" : null}
                >
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Select Document Type"
                    value={doc.document_type}
                    onChange={value => handleDocumentTypeChange(value, index)}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {docType?.map((insurer) => (
                      <Option key={insurer.id} value={insurer.name}>
                        {insurer.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                <Form.Item
                  label="Upload Document"
                  required
                  // Only show error status if submission has been attempted and field is empty
                  validateStatus={isSubmitted && doc.upload_document.length === 0 ? 'error' : ''}
                  help={isSubmitted && doc.upload_document.length === 0 ? "Please Upload Document!" : null}
                >
                  <Dragger
                    maxCount={1}
                    listType="picture"
                    beforeUpload={() => false}
                    accept=".pdf,.png,.jpg,.jpeg"
                    fileList={doc.upload_document}
                    onChange={info => handleUploadChange(info, index)}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Drag and drop a file here or click
                    </p>
                  </Dragger>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <Form.Item label=" ">
                  {index === 0 ? (
                    <Button type="primary" onClick={addDocument}>
                      Add
                    </Button>
                  ) : (
                    <Button type="primary" danger icon={<DeleteOutlined />} onClick={() => removeDocument(doc.key)}>
                      Delete
                    </Button>
                  )}
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Button type="primary" htmlType="submit" loading={loading} style={{ marginTop: '20px' }}>
            Intimate Claim
          </Button>
        </Form>
      </Card>
    </div>
  );
};

export default OpdForm;